import React, {useEffect, useRef} from 'react';
import {css, styled} from 'styled-components';
import {colors} from '../../styles/colors';
import DefaultInput from 'components/common/DefaultInput';
import {useFormatMsg} from 'utils/useFormatMsg';
import DefaultText from './DefaultText';
import {minmax} from 'utils/minmax';
import {createUnicodeCircleNumber} from 'function/converter';
import DefaultImageView from './DefaultImageView';

/**
 * 라벨 입력 컴포넌트의 Props 인터페이스
 * @property {'text' | 'password' | 'phone' | 'number'} type - 입력 타입
 * @property {any} labelText - 라벨 텍스트
 * @property {any} value - 입력값
 * @property {any} [setValue] - 값 변경 핸들러
 * @property {string} [placeholder] - 플레이스홀더 텍스트
 * @property {React.CSSProperties} [placeholderStyle] - 플레이스홀더 스타일
 * @property {boolean} [isVisiblePassword] - 비밀번호 표시 여부
 * @property {React.Dispatch<React.SetStateAction<boolean>>} [setVisiblePassword] - 비밀번호 표시 상태 변경 핸들러
 * @property {boolean} [disabled] - 비활성화 여부
 * @property {string} [min] - 최소값
 * @property {string} [max] - 최대값
 * @property {(e: React.KeyboardEvent<HTMLInputElement>) => void} [onKeyPress] - 키 입력 이벤트 핸들러
 * @property {boolean} [isColumn] - 세로 배치 여부
 * @property {string} [rightImg] - 우측 이미지 경로
 * @property {string} [errorText] - 에러 텍스트
 * @property {boolean} [readOnly] - 읽기 전용 여부
 * @property {() => void} [onClickRightImg] - 우측 이미지 클릭 핸들러
 * @property {React.CSSProperties} [inputStyle] - 입력 필드 스타일
 * @property {() => void} [onClickInput] - 입력 필드 클릭 핸들러
 * @property {React.MutableRefObject<any>} [reference] - 참조 객체
 * @property {React.CSSProperties} [errorTextStyle] - 에러 텍스트 스타일
 * @property {React.CSSProperties} [labelStyle] - 라벨 스타일
 * @property {React.CSSProperties} [labelInputContainerStyle] - 라벨 입력 컨테이너 스타일
 * @property {string} [rightText] - 우측 텍스트
 * @property {() => void} [onClickRightText] - 우측 텍스트 클릭 핸들러
 * @property {React.CSSProperties} [wrapperStyle] - 래퍼 스타일
 * @property {React.CSSProperties} [formattedMessageStyle] - 포맷된 메시지 스타일
 * @property {React.CSSProperties} [textStyle] - 텍스트 스타일
 * @property {boolean} [isFormatValue] - 값 포맷팅 여부
 * @property {number} [index] - 인덱스
 * @property {string} [labelImg] - 라벨 이미지 경로
 * @property {React.CSSProperties} [labelImgStyle] - 라벨 이미지 스타일
 * @property {() => void} [onClickLabelImg] - 라벨 이미지 클릭 핸들러
 * @property {boolean} [errorMode] - 에러 모드 여부
 */
interface ILabelInputProps {
  type: 'text' | 'password' | 'phone' | 'number';
  labelText: any;
  value: any;
  setValue?: any;
  placeholder?: string;
  placeholderStyle?: React.CSSProperties;
  isVisiblePassword?: boolean;
  setVisiblePassword?: React.Dispatch<React.SetStateAction<boolean>>;
  disabled?: boolean;
  min?: string;
  max?: string;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  isColumn?: boolean;
  rightImg?: string;
  errorText?: string;
  readOnly?: boolean;
  onClickRightImg?: () => void;
  inputStyle?: React.CSSProperties;
  onClickInput?: () => void;
  reference?: React.MutableRefObject<any>;
  errorTextStyle?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  labelInputContainerStyle?: React.CSSProperties;
  rightText?: string;
  onClickRightText?: () => void;
  wrapperStyle?: React.CSSProperties;
  formattedMessageStyle?: React.CSSProperties;
  textStyle?: React.CSSProperties;
  isFormatValue?: boolean;
  index?: number;
  labelImg?: string;
  labelImgStyle?: React.CSSProperties;
  onClickLabelImg?: () => void;
  errorMode?: boolean;
}

/**
 * 라벨 입력 컴포넌트
 * 라벨과 입력 필드를 결합한 컴포넌트입니다.
 * @param {ILabelInputProps} props - 라벨 입력 컴포넌트의 props
 * @returns {JSX.Element} 라벨 입력 컴포넌트
 */
const LabelInput = ({
  type,
  labelText,
  value,
  setValue,
  placeholder,
  placeholderStyle,
  isVisiblePassword,
  setVisiblePassword,
  disabled,
  min,
  max,
  onKeyPress,
  isColumn,
  rightImg,
  errorText,
  readOnly,
  onClickRightImg,
  inputStyle,
  onClickInput,
  reference,
  errorTextStyle,
  labelStyle,
  labelInputContainerStyle,
  rightText,
  onClickRightText,
  wrapperStyle,
  formattedMessageStyle,
  isFormatValue,
  index,
  textStyle,
  labelImg,
  labelImgStyle,
  onClickLabelImg,
  errorMode,
}: ILabelInputProps) => {
  const label = useFormatMsg(labelText) || labelText;
  const formatValue = isFormatValue ? useFormatMsg(value) || '' : value;

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (errorMode && inputRef.current) inputRef.current.focus();
  }, [errorMode]);

  return (
    <LabelInputContainer style={labelInputContainerStyle}>
      <LabelInputWrap $isColumn={isColumn}>
        <Label htmlFor={label + value} style={labelStyle} $isColumn={isColumn}>
          <DefaultText
            text={labelText}
            formattedMessageStyle={formattedMessageStyle}
            textStyle={textStyle}
          />
          {index !== undefined && (
            <DefaultText
              text={createUnicodeCircleNumber(index)}
              formattedMessageStyle={formattedMessageStyle}
              textStyle={{marginLeft: '2px'}}
            />
          )}
          {/* {label} */}
          {labelImg && (
            <DefaultImageView
              source={labelImg}
              style={{
                width: minmax(29),
                height: minmax(29),
                marginLeft: minmax(5),
                cursor: 'pointer',
                ...labelImgStyle,
              }}
              onClick={onClickLabelImg}
            />
          )}
        </Label>
        <DefaultInput
          type={type === 'phone' ? 'number' : type}
          value={formatValue}
          setValue={setValue}
          disabled={disabled}
          wrapperStyle={{
            height: minmax(60),
            width: '100%',
            fontSize: minmax(20),
            ...wrapperStyle,
          }}
          id={label + value}
          placeholderText={placeholder}
          placeholderStyle={placeholderStyle}
          isVisiblePassword={isVisiblePassword}
          setVisiblePassword={setVisiblePassword}
          min={min}
          max={max}
          onKeyPress={onKeyPress}
          reference={reference || inputRef}
          inputStyle={{
            borderColor:
              errorText || errorMode ? colors.primary : colors.borderGray,
            // backgroundColor: readOnly ? '#F7F7F7' : colors.white,
            paddingRight: rightImg || rightText ? '20%' : minmax(20),
            cursor: readOnly
              ? rightImg || rightText
                ? 'pointer'
                : 'auto'
              : 'text',
            ...inputStyle,
          }}
          readOnly={readOnly}
          onClick={onClickInput}
        />

        {rightImg && <RightImg src={rightImg} onClick={onClickRightImg} />}
        {rightText && (
          <DefaultText
            text={rightText}
            textStyle={RightTextStyle}
            onClick={onClickRightText}
          />
        )}
      </LabelInputWrap>
      {errorText && (
        <ErrorTextStyle style={errorTextStyle}>
          <DefaultText text={errorText} />
        </ErrorTextStyle>
      )}
    </LabelInputContainer>
  );
};

export default LabelInput;

const LabelInputContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  position: relative;
  min-height: ${minmax(60)};
`;

const LabelInputWrap = styled.div<{$isColumn?: boolean}>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  ${(props: any) =>
    props.$isColumn &&
    css`
      flex-direction: column;
      height: auto;
    `}
`;

const Label = styled.label<{$isColumn?: boolean}>`
  font-size: ${minmax(20)};
  font-weight: 400;
  width: 20%;
  height: 100%;
  padding-right: 2%;
  display: flex;
  justify-content: start;
  align-items: center;
  word-break: break-all;
  ${(props: any) =>
    props.$isColumn &&
    css`
      width: 100%;
      margin-bottom: 12px;
    `}
`;

const ErrorTextStyle = styled.div`
  width: 100%;
  margin-top: 8px;
  text-align: right;
  * {
    font-size: ${minmax(16)};
    color: ${colors.primary};
  }
`;

const RightImg = styled.img`
  width: ${minmax(60)};
  position: absolute;
  right: 0;
  bottom: 0;
  cursor: pointer;
`;

const RightTextStyle: React.CSSProperties = {
  height: '100%',
  position: 'absolute',
  top: 0,
  right: 0,
  display: 'flex',
  alignItems: 'center',
  color: colors.primary,
  paddingRight: minmax(24),
  cursor: 'pointer',
};
